/* eslint no-console: 0 */
import config from '../../config';

/*
 * Browser tracing entry point — deliberately light.
 *
 * This module is imported by both entrypoints and by src/actions/auth.js, so it is in every
 * bundle. It therefore imports nothing from OpenTelemetry: the SDK, zone.js and the
 * instrumentations live in ./telemetrySdk.js, which initTelemetry() requires only when an OTLP
 * endpoint was configured at build time. A build without one never contains that module, so no
 * user pays for tracing that is switched off — no zone.js patching Promise and timers, no
 * wrapped fetch or XHR. See docs/OTEL.md, "Nothing loads when tracing is off".
 *
 * What stays here is the state other modules write before or without tracing: the origins our
 * APIs live on (bookApiURL arrives only after login) and the signed-in caller.
 */

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// The end of an origin: a path, query, fragment, or the end of the string — nothing else. Without
// it `^https://book-api.mhub.my` also matches `https://book-api.mhub.my.evil.example` and a
// different port on the same host.
const ORIGIN_BOUNDARY = '(?:[/?#]|$)';

const originOf = (url) => {
  try {
    return new URL(url).origin;
  } catch (e) {
    return null;
  }
};

/**
 * Matches any request under `url`'s origin, so instrumentation-fetch/xhr inject `traceparent` on
 * our API calls. urlMatches() does an exact string match for a plain string entry, so a RegExp on
 * the origin is required instead of passing the configured URL through as-is.
 */
export const toOriginRegExp = (url) => {
  const origin = originOf(url);
  return origin ? new RegExp(`^${escapeRegExp(origin)}${ORIGIN_BOUNDARY}`) : null;
};

// Origins of the MHub APIs this app calls, in registration order.
const tracedOrigins = [];

/**
 * The live allowlist handed to instrumentation-fetch/-xml-http-request.
 *
 * Deliberately one mutable array rather than a value computed once at init: `bookApiURL` is empty
 * until the login bootstrap resolves it per tenant (src/actions/auth.js), so the Showroom API
 * origin is simply not knowable when initTelemetry() runs. Both instrumentations re-read
 * getConfig().propagateTraceHeaderCorsUrls on every request, so appending to this array after
 * registration takes effect immediately.
 */
const propagateTraceHeaderCorsUrls = [];

/**
 * The live `ignoreUrls` handed to the same two instrumentations: one RegExp matching every URL
 * that is NOT under a traced origin. The browser makes CLIENT spans for our own APIs only —
 * third parties (Cognito, S3, Bugsnag, analytics, …) and static files are not traced at all, so
 * they cost no span and their URLs never reach the collector. Re-read per request like the
 * allowlist; rebuilt in place whenever an origin is registered. Nothing is traced until the first
 * origin is.
 */
const ignoreUrls = [/^/];

/**
 * Adds `url`'s origin to the traceparent allowlist (and so stops ignoring it). Safe to call
 * repeatedly and before initTelemetry(); a URL that does not parse is ignored.
 *
 * Returns true when a new origin was added, which is what the tests assert on.
 */
export const registerTracePropagationOrigin = (url) => {
  if (!url) return false;
  const origin = originOf(url);
  if (!origin || tracedOrigins.indexOf(origin) !== -1) return false;

  tracedOrigins.push(origin);
  propagateTraceHeaderCorsUrls.push(toOriginRegExp(origin));
  ignoreUrls[0] = new RegExp(
    `^(?!(?:${tracedOrigins.map(escapeRegExp).join('|')})${ORIGIN_BOUNDARY})`);
  return true;
};

/** Whether `url` is under one of the API origins registered above. */
export const isTracedUrl = url => tracedOrigins.indexOf(originOf(url)) !== -1;

// Exported for tests only — the array identities are what make the live lists work.
export const getTracePropagationOrigins = () => propagateTraceHeaderCorsUrls;
export const getIgnoredUrls = () => ignoreUrls;
export const resetTracePropagationOrigins = () => {
  tracedOrigins.length = 0;
  propagateTraceHeaderCorsUrls.length = 0;
  ignoreUrls[0] = /^/;
};

// The authenticated caller, stamped on root spans by the SDK's EndUserSpanProcessor. Late-bound
// for the same reason the allowlist is: initTelemetry() runs before render, and nobody has logged
// in yet, so identity cannot be a resource attribute — resources are fixed at init.
let endUser = null;
// Bumped by every setEndUser / clearEndUser, so an ID-token read that resolves late cannot
// bring back a caller who has since logged out or been replaced.
let endUserGeneration = 0;
// Set once startTracing() has run. Until then identifyEndUser() reads no token: an untraced
// build, or a browser that fell back to untraced, makes no extra Cognito call.
let tracingStarted = false;

/** The ID token's `sub` claim, or null when there is no usable one. */
const tokenSubject = (claims) => {
  const sub = claims ? claims.sub : undefined;
  return typeof sub === 'string' && sub !== '' ? sub : null;
};

/**
 * Records who is using the app so a browser span is filterable by caller in SigNoz.
 *
 * `user.id` is the `sub` claim of the caller's Cognito ID token — the value mhub-book-api and
 * mhub-api put on their SERVER spans (OTEL-94: the verified ID token's sub), so a trace carries
 * one user.id from browser to database. It is never the Cognito username: for an SSO user that
 * is `<provider>_<email>` (Staffs/Overview.jsx relies on exactly that), which would put the
 * email on every span. Without a `sub` there is no user.id at all, not a substitute.
 *
 * `mhub.user_id` and `mhub.company_id` are numbers, matching the backend's AttrUserID.Int /
 * AttrCompanyID.Int, so one SigNoz query covers browser and API.
 *
 * Deliberately no email and no name. Every attribute here ships from the browser to the
 * collector on every span, so the rule for introducing one — bounded cardinality, no PII —
 * applies with full force. Note also that anything stamped here is client-controlled: it is
 * filter metadata, never an authorization input. The backend's own user.id is the
 * trustworthy one.
 *
 * Inert when tracing is disabled — nothing reads it.
 */
export const setEndUser = (payload, idTokenClaims) => {
  if (!payload) return;
  endUserGeneration += 1;
  const attributes = {};

  const subject = tokenSubject(idTokenClaims);
  if (subject) {
    attributes['user.id'] = subject;
  }
  const userID = Number(payload.id);
  if (Number.isFinite(userID) && userID !== 0) {
    attributes['mhub.user_id'] = userID;
  }
  const companyID = Number(payload.affiliate && payload.affiliate.company ?
    payload.affiliate.company.id : undefined);
  if (Number.isFinite(companyID) && companyID !== 0) {
    attributes['mhub.company_id'] = companyID;
  }

  // An absent attribute and an empty one are different things in SigNoz, so a payload that
  // carries none of these leaves the spans unstamped rather than stamping blanks.
  endUser = Object.keys(attributes).length > 0 ? attributes : null;
};

/**
 * setEndUser for a login: the numeric ids at once, then user.id once the ID token is read.
 *
 * `readIdTokenClaims` returns (a promise of) the decoded Cognito ID token; auth.js passes
 * Amplify's currentSession(), the token fetchM sends to the APIs. It is only called while tracing
 * runs. If it fails, or the token has no `sub`, user.id stays unset. The returned promise settles
 * when that is decided — callers need not wait for it; tests do.
 */
export const identifyEndUser = (payload, readIdTokenClaims) => {
  setEndUser(payload);
  if (!payload || !tracingStarted) return Promise.resolve();

  const generation = endUserGeneration;
  return Promise.resolve()
    .then(readIdTokenClaims)
    .then((claims) => {
      // A logout or a newer login since the read began owns the caller now.
      if (generation === endUserGeneration) setEndUser(payload, claims);
    })
    .catch((e) => {
      const reason = e && e.message ? e.message : e;
      console.warn('[telemetry] user.id unset: the ID token could not be read', reason);
    });
};

/** Clears the caller on logout, so the next session's spans cannot inherit the last user. */
export const clearEndUser = () => {
  endUser = null;
  endUserGeneration += 1;
};

export const getEndUser = () => endUser;

export const DEFAULT_SAMPLER_RATIO = 1;

/**
 * Reads OTEL_TRACES_SAMPLER_ARG: the fraction of traces this browser starts that are recorded.
 *
 * Unset means 1 — every trace, the same default the Go tiers run with. Anything that is not a
 * number from 0 to 1 also means 1, with a warning, which is what the OpenTelemetry SDKs do with
 * a bad ratio: a typo in a build variable must not silently switch tracing off.
 */
export const parseSamplerRatio = (raw) => {
  if (raw === undefined || raw === null || String(raw).trim() === '') return DEFAULT_SAMPLER_RATIO;
  const ratio = Number(raw);
  if (!Number.isFinite(ratio) || ratio < 0 || ratio > 1) {
    console.warn(`[telemetry] OTEL_TRACES_SAMPLER_ARG "${raw}" is not a number from 0 to 1; `
      + 'sampling every trace');
    return DEFAULT_SAMPLER_RATIO;
  }
  return ratio;
};

/**
 * Initialises browser tracing. Returns whether it started. Never throws — tracing must never be
 * able to take the app down (fail open).
 *
 * `routes` is the react-router route element; navigation spans are named from it.
 */
export const initTelemetry = (routes) => {
  // The build-time gate. DefinePlugin inlines OTEL_EXPORTER_OTLP_ENDPOINT, so in a build without
  // an endpoint this test is the literal "" and webpack drops the whole block, require() included:
  // ./telemetrySdk.js, zone.js and the instrumentations are then not in the bundle at all. It has
  // to stay an inline `process.env.…` test — webpack only folds a constant it can see right here.
  if (process.env.OTEL_EXPORTER_OTLP_ENDPOINT) {
    const endpoint = config.get('otelEndpoint');
    if (!endpoint) return false;

    try {
      // instrumentation-fetch reads TextEncoder while its module evaluates, so this check has to
      // come before the require below: an older browser stays on the untraced path rather than
      // failing to load the app.
      if (typeof TextEncoder === 'undefined' || typeof TextDecoder === 'undefined') {
        console.warn('[telemetry] tracing disabled: TextEncoder/TextDecoder unavailable');
        return false;
      }

      // The browser is the fan-out point: fetchM.js talks to mhub-api via apiURL and to
      // mhub-book-api via bookApiURL. Miss either and half the traces silently orphan.
      // bookApiURL is usually still empty here — auth.js registers it once login resolves it.
      registerTracePropagationOrigin(config.get('apiURL'));
      registerTracePropagationOrigin(config.get('bookApiURL'));

      // eslint-disable-next-line global-require
      const { startTracing } = require('./telemetrySdk');
      startTracing({
        endpoint,
        routes,
        samplerRatio: parseSamplerRatio(config.get('otelSamplerRatio')),
        propagateTraceHeaderCorsUrls,
        ignoreUrls,
        isTracedUrl,
        getEndUser,
      });
      tracingStarted = true;
      return true;
    } catch (e) {
      // Fail open on ANY initialisation failure, not just the ones we can name. Both entrypoints
      // call this before render(), so an uncaught exception here is a blank application — and the
      // exporter does throw on configuration it dislikes: an endpoint with no scheme raises
      // "Could not parse user-provided export URL" synchronously. startTracing() registers
      // nothing before that throw, so there is no half-wired provider.
      console.warn('[telemetry] tracing disabled: initialisation failed', e);
      return false;
    }
  }
  return false;
};

export default initTelemetry;



// WEBPACK FOOTER //
// ./src/lib/telemetry.js